import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight, ChevronRight, FileText, Info, Printer, Share2, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
// Discussion section hidden for now
// import GmpDiscussion from '../components/gmp/GmpDiscussion';
import SmeReviewStatCards from '../components/sme-reviews/SmeReviewStatCards';
import SmeRatingOverview from '../components/sme-reviews/SmeRatingOverview';
import SmeReviewsTable from '../components/sme-reviews/SmeReviewsTable';
import SmeReviewsSidebar from '../components/sme-reviews/SmeReviewsSidebar';
import { buildSmeIpoRows } from '../utils/smeIpos';
import { brokerNames, buildSmeReviewsCsv, computeSmeReviewStats, filterSmeReviews, mapSmeReviewItems } from '../utils/smeReviews';
import { formatFilingDate } from '../utils/upcomingIpos';
import { todayIso } from '../utils/ipoCalendar';
import { fetchSmeReviews } from '../store/slices/smeReviewsSlice';
import { fetchSmeUpcomingIpos } from '../store/slices/upcomingIposSlice';
import { fetchGmp, selectGmpBoardRows } from '../store/slices/gmpSlice';

const NOTICE_MS = 4500;
const PAGE_SIZE = 25;
const DATA_MAX_AGE_MS = 60000;
const YEAR = String(new Date().getFullYear());
const HEADING = `SME IPO Reviews ${YEAR}, IPO Analysis and Notes`;

// const DISCUSSION_COPY = {
//   headingId: 'sme-reviews-discussion-heading',
//   title: 'Investor Discussion & SME Reviews',
//   subtitle: 'Discuss SME valuations, broker consensus and subscription expectations with fellow investors.',
//   placeholder: `Add a public comment or ask a question about ${YEAR} SME IPO valuations, broker consensus or subscription safety...`,
//   submitLabel: 'Post Comment',
//   startLabel: 'New Message',
// };

export const SmeReviewsPage = () => {
  const dispatch = useDispatch();
  const today = useMemo(() => todayIso(), []);
  const [query, setQuery] = useState('');
  const [broker, setBroker] = useState('all');
  const [verdict, setVerdict] = useState('all');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState(null);
  const tableRef = useRef(null);

  // GET /api/core/ipos/sme/reviews: broker calls for every SME issue in one request
  const reviewItems = useSelector((state) => state.smeReviews.items);
  const reviewsStatus = useSelector((state) => state.smeReviews.status);
  const reviewsError = useSelector((state) => state.smeReviews.error);
  const reviewsRefreshedAt = useSelector((state) => state.smeReviews.refreshedAt);
  // The SME schedule and GMP feeds add platform, bidding status and trending GMP
  const scheduleItems = useSelector((state) => state.smeUpcomingIpos.items);
  const gmpRows = useSelector(selectGmpBoardRows);

  useEffect(() => {
    dispatch(fetchSmeReviews({ maxAgeMs: DATA_MAX_AGE_MS }));
    dispatch(fetchSmeUpcomingIpos({ maxAgeMs: DATA_MAX_AGE_MS }));
    dispatch(fetchGmp({ maxAgeMs: DATA_MAX_AGE_MS }));
  }, [dispatch]);

  const smeIpos = useMemo(() => buildSmeIpoRows({ liveIpos: scheduleItems, gmpRows }, today), [scheduleItems, gmpRows, today]);
  const SME_REVIEWS = useMemo(() => mapSmeReviewItems({ items: reviewItems, smeIpos }), [reviewItems, smeIpos]);
  const stats = useMemo(() => computeSmeReviewStats(SME_REVIEWS), [SME_REVIEWS]);
  const brokers = useMemo(() => brokerNames(SME_REVIEWS), [SME_REVIEWS]);
  const rows = useMemo(() => filterSmeReviews(SME_REVIEWS, { query, broker, verdict }), [SME_REVIEWS, query, broker, verdict]);
  const lastUpdated = useMemo(
    () => (reviewsRefreshedAt ? reviewsRefreshedAt.slice(0, 10) : SME_REVIEWS.map((row) => row.updatedOn).filter(Boolean).sort().pop() || null),
    [reviewsRefreshedAt, SME_REVIEWS]
  );

  const isLoading = !SME_REVIEWS.length && (reviewsStatus === 'idle' || reviewsStatus === 'loading');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADING} | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [query, broker, verdict]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const clearFilters = () => {
    setQuery('');
    setBroker('all');
    setVerdict('all');
  };

  const changePage = (nextPage) => {
    setPage(nextPage);
    if (tableRef.current && tableRef.current.getBoundingClientRect().top < 0) {
      tableRef.current.scrollIntoView({ block: 'start' });
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: HEADING, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setNotice('Link to this page copied to your clipboard.');
    } catch {
      // Share sheet dismissed or clipboard blocked: nothing to do
    }
  };

  const handleExport = () => {
    // BOM so spreadsheet apps read the file as UTF-8
    const blob = new Blob(['﻿', buildSmeReviewsCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sme-ipo-reviews-${YEAR}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  let emptyMessage;
  if (reviewsStatus === 'failed' && !SME_REVIEWS.length) {
    emptyMessage = (
      <>
        SME broker reviews are temporarily unavailable{reviewsError ? ` (${reviewsError})` : ''}.{' '}
        <button type="button" onClick={() => dispatch(fetchSmeReviews())} className="font-bold text-[#008080] hover:underline">
          Retry
        </button>
      </>
    );
  } else if (!SME_REVIEWS.length) {
    emptyMessage = (
      <>
        No SME broker reviews have been published yet. They appear here as brokerages release their notes —{' '}
        <Link to="/sme-upcoming-ipos" className="font-bold text-[#008080] hover:underline">
          see upcoming SME IPOs
        </Link>
        .
      </>
    );
  } else {
    emptyMessage = (
      <>
        No SME reviews match these filters.{' '}
        <button type="button" onClick={clearFilters} className="font-bold text-[#008080] hover:underline">
          Clear filters
        </button>
      </>
    );
  }

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
                <Link to="/" className="hover:text-[#008080] transition-colors">
                  Home
                </Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <Link to="/sme-ipos" className="hover:text-[#008080] transition-colors">
                  SME IPOs
                </Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="text-slate-900 font-semibold" aria-current="page">
                  {HEADING}
                </span>
              </nav>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                Research feed{lastUpdated ? `: updated ${formatFilingDate(lastUpdated)}` : ''}
              </span>
            </div>

            <span className="mt-4 inline-flex px-2.5 py-1 rounded-md border border-teal-100 bg-teal-50 text-[11px] font-bold text-[#006666]">
              CY {YEAR} Universe
            </span>

            <div className="mt-2 flex flex-col xl:flex-row xl:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">{HEADING}</h1>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Brokerage consensus, research notes and independent analyst recommendations for BSE SME &amp; NSE Emerge
                  public offerings, with Capital Market (CM) quantitative ratings where published.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 shadow-sm transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Share Report
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#008080] hover:bg-[#006666] text-xs font-bold text-white shadow-sm transition-colors"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Export {YEAR} PDF Note
                </button>
              </div>
            </div>
          </div>

          <SmeReviewStatCards stats={stats} year={YEAR} />

          {/* Sidebar sits beside the content from 1280px; narrower screens stack it below */}
          <div className="grid grid-cols-1 min-[1280px]:grid-cols-[minmax(0,1fr)_300px] gap-5 sm:gap-6 items-start">
            <div className="space-y-5 sm:space-y-6 min-w-0">
              <SmeRatingOverview />

              <SmeReviewsTable
                ref={tableRef}
                rows={rows}
                smeCount={SME_REVIEWS.length}
                year={YEAR}
                page={page}
                pageSize={PAGE_SIZE}
                onPageChange={changePage}
                query={query}
                onQueryChange={setQuery}
                broker={broker}
                onBrokerChange={setBroker}
                brokers={brokers}
                verdict={verdict}
                onVerdictChange={setVerdict}
                onExport={handleExport}
                isLoading={isLoading}
                emptyMessage={emptyMessage}
              />

              <Link
                to="/ipo-reviews"
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-[#006666] to-[#008080] p-5 sm:p-6 text-white shadow-sm hover:shadow-card transition-shadow"
              >
                <span className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </span>
                  <span>
                    <span className="block text-base font-extrabold">View All SME &amp; Mainboard IPO Reports</span>
                    <span className="block text-xs text-teal-50/90">
                      Broker calls, consensus verdicts and research notes for every IPO in one place.
                    </span>
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 self-start sm:self-auto px-4 py-2 rounded-xl bg-white text-[#006666] text-xs font-bold whitespace-nowrap">
                  View All Reports
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>

              {/* Discussion section hidden for now: <GmpDiscussion initialThreads={[]} copy={DISCUSSION_COPY} /> */}

            </div>

            <SmeReviewsSidebar smeIpos={smeIpos} />
          </div>
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default SmeReviewsPage;
